import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from 'environments-lib';
import { IEnvironment } from 'interfaces-lib';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { delay } from 'rxjs/operators';
import { Identity, IdentityStorage } from 'storage-service-lib';
import { IAnalyticsService } from './ianalyticsservice';

// TODO: POINT MANAGER/WORD ADDIN/AIA/NT AT THIS LIB!

interface IPendoWindow extends Window {
  pendo?: any;
}
declare const window: IPendoWindow;

@Injectable({
  providedIn: 'root',
})
export class PendoService implements IAnalyticsService {
  identity: Identity | undefined;
  pendoReady = false;

  /* This can be used/adapted for any events that might need to be sent */
  private pendoEvent: IAnalyticsService | undefined;

  constructor(@Inject(ENVIRONMENT) protected environment: Partial<IEnvironment>, private identityStorage: IdentityStorage) {}

  initPendoScript(productAPIKey: string, pendoObject?: any): void {
    if (this.environment.enableAnalytics) {
      this.injectPendo(productAPIKey);

      if (pendoObject && !this.pendoReady) {
        this.initPendo(pendoObject);
      } else {
        this.identityStorage
          .observe()
          .pipe(delay(250))
          .subscribe((identity) => {
            this.identity = identity;
            if (this.identity?.tenant !== '' && this.identity?.token !== '' && !this.pendoReady) {
              this.initPendo();
            }
          });
      }
    }
  }

  private injectPendo(productAPIKey: string): void {
    (function (p: any, e: Document, n: string, d: string, o?: any): void {
      const v: string[] = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
      let w: number, x: number, y: HTMLScriptElement, z: Node | null;
      o = p[d] = p[d] || {};
      o._q = o._q || [];
      for (w = 0, x = v.length; w < x; ++w) {
        ((m: string) => {
          o[m] =
            o[m] ||
            function (...args: any[]): void {
              o._q[m === v[0] ? 'unshift' : 'push']([m].concat(args));
            };
        })(v[w]);
      }
      y = e.createElement(n) as HTMLScriptElement;
      y.async = true;
      y.src = `https://cdn.eu.pendo.io/agent/static/${productAPIKey}/pendo.js`;
      z = e.getElementsByTagName(n)[0];
      if (z && z.parentNode) {
        z.parentNode.insertBefore(y, z);
      }
    })(window as any, document, 'script', 'pendo');
  }

  private initPendo(pendoObject?: any): void {
    if (window.pendo) {
      if (pendoObject) {
        window.pendo.initialize({
          pendoObject,
        });
        this.pendoReady = true;
      } else {
        if (this.identity?.token) {
          const decoded: any = jwtDecode<JwtPayload>(this.identity?.token);
          const userId = decoded?.userId;
          const tenantUniqueId = decoded?.corporationId || decoded?.organizationId;
          if (userId && tenantUniqueId) {
            window.pendo.initialize({
              visitor: { id: `${userId}-${tenantUniqueId}` },
              account: {
                id: `${this.identity.namespace}-${tenantUniqueId}`,
                name: this.identity.tenant,
              },
            });
            this.pendoReady = true;
          }
        }
      }
    }
  }
  /**
   * Records a custom event to Pendo.
   * Implements IAnalyticsService.recordEvent.
   * @param eventType - The name for the event being recorded.
   * @param eventData - A string/object containing the event's attributes.
   *
   * @example
   * this.pendoService?.recordEvent('Preview and Edit button', metaData);
   * this.pendoService?.recordEvent('Copied Text', { message: copiedText });
   * this.pendoService?.recordEvent('Copied Text', string);
   */
  recordEvent(eventType: string, eventData: string | object = {}): void {
    if (typeof window.pendo !== 'undefined' && typeof window.pendo.track === 'function') {
      let eventDataAsObject: object;

      if (typeof eventData === 'string') {
        eventDataAsObject = { message: eventData };
      } else {
        eventDataAsObject = eventData;
      }

      window.pendo.track(eventType, eventDataAsObject);
    }
  }

  recordError(error: any): void {
    // Pendo doesn’t handle error events, so just log to console if needed
    console.warn('recordError called, but PendoService does not track errors.', error);
  }

  /**
   * Truncates a value to fit within Pendo's property size limits (512bytes)
   * @param value - The value to truncate
   * @param maxLength - Maximum length (default: 50)
   * @returns Truncated string with [...] suffix if exceeded, or empty string if null/undefined
   *
   * @example
   * this.pendoService.truncateForPendo('A very long description that exceeds the limit');
   * // Returns: 'A very long description that exceeds the [...]'
   *
   * this.pendoService.truncateForPendo(someValue, 100); // Custom max length
   */
  truncateForPendo(value: any, maxLength: number = 50): string {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    if (stringValue.length <= maxLength) {
      return stringValue;
    }
    return stringValue.substring(0, maxLength - 5) + '[...]';
  }
}
